/**
 * Patients hooks (TanStack Query)
 *
 * Lists patients and registers new ones. Registration is optimistic with
 * rollback, mirroring the session-mutation pattern in useSessions.ts.
 */

import { useMemo } from 'react';
import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import i18n from '@/i18n';
import {
  listPatients,
  registerPatient,
  getPatient,
  getPatientsSchema,
  getPatientSchema,
  getPatientWithRevision,
  putPatient,
  relinkAdmissionRef,
  discloseAdmissionRef,
  type PatientResponse,
  type RegisterPatientRequest,
  type PatientStatusFilter,
  type AdmissionRefLink,
  type DiscloseAdmissionRefRequest,
  type DiscloseAdmissionRefResponse,
} from '@/api/endpoints/patients';
import { errorToast } from '@/hooks/errorToast';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import { useConfigStore } from '@/stores/config';
import { QUERY_STALE_TIMES } from '@/lib/query-config';
import { ApiError } from '@/api/client';
import type {
  PatientsConfig,
  PatientAttributeField,
} from '@/api/types';
import { randomUUID } from '@/lib/uuid';
import { writableFields, type PatientSchemaDocument } from '@/api/patientSchema';

const DEFAULT_STATUS_FILTER: PatientStatusFilter = 'in_progress';

export const patientKeys = {
  all: ['patients'] as const,
  lists: () => [...patientKeys.all, 'list'] as const,
  list: (status: PatientStatusFilter = DEFAULT_STATUS_FILTER) => [...patientKeys.lists(), status] as const,
  detail: (token: string) => [...patientKeys.all, 'detail', token] as const,
  /** The read that carries the entity tag a conditional write sends back. Keyed
   *  apart from `detail` so invalidating it re-reads the TAG too. */
  revision: (token: string) => [...patientKeys.all, 'revision', token] as const,
};

export const patientAttributeSchemaKeys = {
  all: ['patientAttributeSchema'] as const,
  location: (locationId: string) => [...patientAttributeSchemaKeys.all, locationId] as const,
  patient: (token: string) => [...patientAttributeSchemaKeys.all, 'patient', token] as const,
};

/**
 * Patient registration config from the config store.
 * Returns null until /config has loaded (or if the backend omits the block),
 * so callers must handle the loading/absent state explicitly.
 */
export function usePatientsConfig(): PatientsConfig | null {
  return useConfigStore((s) => s.config?.patients ?? null);
}

/**
 * List patients visible to the current user. `status` omitted ⇒ the server's
 * `in_progress` default (AIP-164); pass `'all'` for the census board's "Show
 * discharged" toggle. Each status filter caches under its own query key, so
 * toggling the filter never shows a stale cross-filter list mid-refetch.
 */
export function usePatients(status?: PatientStatusFilter) {
  return useAuthenticatedQuery<PatientResponse[]>({
    queryKey: patientKeys.list(status ?? DEFAULT_STATUS_FILTER),
    queryFn: () => listPatients(status),
    retry: 1,
    retryDelay: 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });
}

/**
 * One patient by token — the only response that carries
 * `admission_ref_link.can_disclose`, since a list never pays the per-patient
 * ACL check behind it.
 */
export function usePatientDetail(token: string | null | undefined) {
  return useAuthenticatedQuery<PatientResponse>({
    queryKey: patientKeys.detail(token ?? 'none'),
    queryFn: () => getPatient(token as string),
    enabled: !!token,
    retry: 1,
    retryDelay: 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
}

/**
 * The tailored schema document for /patients at a unit: what THIS caller writes
 * and reads there. Cached on the same config-driven stale window as
 * `useLocations` (org/roles vocabulary) — a unit's shape changes about as often
 * as the org tree itself, far less than the census.
 */
export function usePatientsSchema(locationId: string | null | undefined) {
  return useAuthenticatedQuery<PatientSchemaDocument>({
    queryKey: patientAttributeSchemaKeys.location(locationId ?? 'none'),
    queryFn: () => getPatientsSchema(locationId as string),
    enabled: !!locationId,
    staleTime: QUERY_STALE_TIMES.users,
    refetchOnWindowFocus: false,
  });
}

/**
 * The tailored schema document for ONE patient, resolved from that patient's
 * own placement rather than from a unit the caller names. A transfer therefore
 * changes the shape an edit form renders with nothing on the record changing —
 * which is the whole point of a schema being a property of (resource ×
 * profession × unit) and not of a stored revision.
 */
export function usePatientSchema(token: string | null | undefined) {
  return useAuthenticatedQuery<PatientSchemaDocument>({
    queryKey: patientAttributeSchemaKeys.patient(token ?? 'none'),
    queryFn: () => getPatientSchema(token as string),
    enabled: !!token,
    staleTime: QUERY_STALE_TIMES.users,
    refetchOnWindowFocus: false,
  });
}

/**
 * One patient AND the entity tag of the read that produced it.
 *
 * A conditional write needs the tag of the read its BODY was computed from.
 * Fetching a fresh tag immediately before the write instead would send a tag
 * nothing was computed against, which makes `If-Match` always match and turns
 * a colleague's concurrent edit into a silent overwrite — the exact lost update
 * the header exists to refuse. So the tag travels with the values, from here,
 * and a 412 is a real conflict the form re-reads on.
 */
export function usePatientWithRevision(token: string | null | undefined) {
  return useAuthenticatedQuery<{ patient: PatientResponse; etag: string | null }>({
    queryKey: patientKeys.revision(token ?? 'none'),
    queryFn: () => getPatientWithRevision(token as string),
    enabled: !!token,
    retry: 1,
    retryDelay: 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
}

/**
 * The WRITABLE fields for a unit — the controls a write form renders.
 *
 * Read off `$defs.create`, so a server-minted field is absent by construction
 * and the form never has to know a strategy name to leave one out. An empty
 * list before the document loads is the honest state: there is no
 * deployment-wide floor to fall back on any more, because which attributes
 * exist depends on the unit AND on the caller's profession, and no config block
 * can state that.
 */
export function useEffectivePatientAttributeFields(
  locationId: string | null | undefined,
): PatientAttributeField[] {
  const { data } = usePatientsSchema(locationId);
  return useMemo(() => writableFields(data), [data]);
}

interface RegisterPatientContext {
  /** One snapshot per MOUNTED status-filtered list cache (in_progress, all,
   *  …) — restored independently on rollback so a nurse viewing status=all
   *  during a failed registration sees exactly what THAT cache held, not
   *  just the default filter's. */
  previousLists: Array<[QueryKey, PatientResponse[] | undefined]>;
  tempToken: string;
}

/**
 * Register a patient. Optimistic: prepends a placeholder to every MOUNTED
 * list cache whose status filter would actually include a fresh registration
 * (in_progress and all — never a hypothetical discharged/cancelled-only
 * cache), replaces it with the server response on success, rolls back every
 * touched cache on error. `setQueriesData`'s updater form doesn't expose
 * which query it's touching, so this iterates `getQueriesData` explicitly
 * (mirrors `useUpdatePatientAttributes`'s all-caches intent, but that hook
 * doesn't need the per-key branching since a patch never changes which list
 * a patient belongs in).
 */
export function useRegisterPatient() {
  const queryClient = useQueryClient();

  return useMutation<
    PatientResponse,
    Error,
    RegisterPatientRequest,
    RegisterPatientContext
  >({
    mutationFn: (body) => registerPatient(body),
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: patientKeys.lists() });
      const previousLists = queryClient.getQueriesData<PatientResponse[]>({
        queryKey: patientKeys.lists(),
      });

      const tempToken = `temp-${randomUUID()}`;
      const optimistic: PatientResponse = {
        token: tempToken,
        attributes: body.attributes,
        // Registration always opens the first encounter (add = create + place),
        // so in_progress is a safe optimistic guess; location_id mirrors the
        // caller's choice when one was made (undefined ⇒ server falls back to
        // the caller's home unit) — both are corrected by the real response.
        status: 'in_progress',
        location_id: body.location_id ?? null,
      };
      for (const [key, data] of previousLists) {
        const status = key[key.length - 1];
        if (status !== 'in_progress' && status !== 'all') continue;
        queryClient.setQueryData<PatientResponse[]>(key, [optimistic, ...(data ?? [])]);
      }

      return { previousLists, tempToken };
    },
    onSuccess: (created, _body, context) => {
      // Replace the optimistic placeholder with the authoritative record in
      // every list cache it was inserted into.
      queryClient.setQueriesData<PatientResponse[]>(
        { queryKey: patientKeys.lists() },
        (old) => old?.map((p) => (p.token === context.tempToken ? created : p)),
      );
    },
    onError: (_err, _body, context) => {
      if (context) {
        for (const [key, data] of context.previousLists) {
          queryClient.setQueryData(key, data);
        }
      }
      errorToast({
        context: 'registerPatient',
        description: i18n.t('patients.register.error', {
          defaultValue: 'Could not register the patient.',
        }),
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: patientKeys.lists() });
    },
  });
}

interface RelinkAdmissionRefVariables {
  token: string;
  /** The declared link_fields keyed by name (design §4 POST
   *  /patients/{id}/admission-ref/link). */
  link: Record<string, string>;
}

/**
 * Correct (or first-establish) a patient's admission_ref linkage. Updates
 * the detail cache's `admission_ref_link` metadata in place on success — the
 * token itself never changes, and no list surface carries linkage metadata,
 * so no other cache is touched. 422 is field-level (rendered inline by the
 * caller, e.g. an unknown link key); anything else gets the generic toast,
 * mirroring `useUpdatePatientAttributes`.
 */
export function useRelinkAdmissionRef() {
  const queryClient = useQueryClient();

  return useMutation<AdmissionRefLink, Error, RelinkAdmissionRefVariables>({
    mutationFn: ({ token, link }) => relinkAdmissionRef(token, link),
    onSuccess: (link, { token }) => {
      queryClient.setQueryData<PatientResponse>(patientKeys.detail(token), (old) =>
        old ? { ...old, admission_ref_link: link } : old,
      );
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 422) return;
      errorToast({
        context: 'relinkAdmissionRef',
        description: i18n.t('patientAdmin.admissionRef.linkError', {
          defaultValue: 'Could not save the admission link. Please try again.',
        }),
      });
    },
  });
}

interface DiscloseAdmissionRefVariables extends DiscloseAdmissionRefRequest {
  token: string;
}

/**
 * Reveal a patient's admission_ref link content (design §4 — "THE one
 * door"). Deliberately carries NO onSuccess cache write: the returned
 * `link` content must appear on exactly one surface (the calling dialog,
 * rendered once) and never land in TanStack Query's cache, a store, or
 * anywhere a background refetch or devtools inspector could later surface
 * it (property P3 — "the link never leaks"). 404/422 are handled inline by
 * the caller (unlinked/unauthorized, bad reason code, justification
 * required) — only a genuine network/5xx failure gets the generic toast.
 */
export function useDiscloseAdmissionRef() {
  return useMutation<DiscloseAdmissionRefResponse, Error, DiscloseAdmissionRefVariables>({
    mutationFn: ({ token, ...body }) => discloseAdmissionRef(token, body),
    onError: (err) => {
      if (err instanceof ApiError && (err.status === 422 || err.status === 404)) return;
      errorToast({
        context: 'discloseAdmissionRef',
        description: i18n.t('patientAdmin.admissionRef.revealFailed', {
          defaultValue: 'Could not reveal the admission link. Please try again.',
        }),
      });
    },
  });
}

interface UpdatePatientAttributesVariables {
  token: string;
  /** The caller's COMPLETE writable view — every field it should end up
   *  holding. A field OMITTED here is CLEARED; there is no `null`. */
  attributes: Record<string, unknown>;
  /** The entity tag of the read `attributes` was computed from, sent as
   *  `If-Match` (`usePatientWithRevision`). */
  etag: string;
}

/**
 * Replace the caller's writable view of a patient. Server-authoritative — NO
 * optimistic update: a 422 carries per-field violations the caller renders
 * inline, so the form must keep showing exactly what the nurse typed until the
 * server confirms or rejects it; an optimistic write-then-rollback would flash
 * a value the server never accepted. Updates every cached list variant
 * (in_progress/all) and the detail cache in place on success so the census +
 * detail panel never show a stale attribute after a save.
 *
 * `etag` comes from the read the body was computed from
 * (`usePatientWithRevision`) and is NOT re-fetched here. Re-reading the tag
 * immediately before the write would make `If-Match` match every time, which
 * turns a colleague's concurrent edit into a silent overwrite: the caller sends
 * a complete view, so whatever that colleague changed is replaced by values
 * computed before it existed. A 412 is therefore reachable and MEANS something
 * — the caller re-reads and re-applies.
 */
export function useUpdatePatientAttributes() {
  const queryClient = useQueryClient();

  return useMutation<PatientResponse, Error, UpdatePatientAttributesVariables>({
    mutationFn: ({ token, attributes, etag }) => putPatient(token, attributes, etag),
    onSuccess: (updated) => {
      queryClient.setQueriesData<PatientResponse[]>({ queryKey: patientKeys.lists() }, (old) =>
        old?.map((p) => (p.token === updated.token ? updated : p)),
      );
      queryClient.setQueryData(patientKeys.detail(updated.token), updated);
    },
    onError: (err, { token }) => {
      // 412 means somebody else wrote between the read and this save. The tag
      // the caller holds is dead, so the read behind it is invalidated here —
      // one place, so no caller can re-submit against a stale tag and loop.
      // The caller renders the conflict; a toast on top would say less.
      if (err instanceof ApiError && err.status === 412) {
        void queryClient.invalidateQueries({ queryKey: patientKeys.revision(token) });
        void queryClient.invalidateQueries({ queryKey: patientKeys.detail(token) });
        return;
      }
      // 422 is field-level validation the caller renders inline (per-field,
      // next to the offending input) — a generic toast on top would be
      // redundant noise. Anything else (network/5xx) gets the toast, since
      // there's no inline surface for it.
      if (err instanceof ApiError && err.status === 422) return;
      errorToast({
        context: 'updatePatientAttributes',
        description: i18n.t('patientAdmin.errors.updateAttributesFailed', {
          defaultValue: 'Could not save the attributes. Please try again.',
        }),
      });
    },
    onSettled: (_data, _err, { token }) => {
      void queryClient.invalidateQueries({ queryKey: patientKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: patientKeys.revision(token) });
    },
  });
}
